<template>
  <div class="brand-fetcher">
    <div class="input-container">
      <input v-model="brandDomain" type="text" :placeholder="$t('brandFetcher.enterCompanyEmail')"
        @click="handleInputClick" @keydown.enter="handleFetchBrandData" :class="{ pulsating: isFirstVisit && !isSettings }" />
    </div>

    <div class="button-container">
      <button class="action-button" v-if="!fetched" @click="handleFetchBrandData"
        :disabled="!brandDomain || isFetching">
        <template v-if="!isFetching">
          {{ $t('brandFetcher.syncWithBrandData') }}
        </template>
        <template v-else>
          <i class="fas fa-spinner fa-spin"></i>
        </template>
      </button>

      <button class="action-button" v-else @click="handleRollBrandData" :disabled="!brandDomain">
        {{ $t('brandFetcher.changeColors') }}
      </button>
    </div>

    <div v-if="errorMessage" class="brand-error-toast">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { BACKEND_URL } from "@/environments";

export default {
  name: "BrandFetcher",
  props: {
    formData: {
      type: Object,
      default: () => ({}),
    },
    isSettings: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      brandDomain: "",
      brandData: null,
      fetched: false,
      lastFetchedDomain: "",
      isFirstVisit: !localStorage.getItem("brandFetcherInputClicked"),
      errorMessage: "",
      token: localStorage.getItem('generatorAuthToken'),
      isFetching: false,
      originalEmail: "",
    };
  },
  watch: {
    brandDomain(newVal) {
      if (newVal !== this.lastFetchedDomain) {
        this.fetched = false;
        this.errorMessage = '';
      }
    },
  },
  computed: {
    copyLinkUrl() {
      let domain = this.brandDomain;
      if (domain.includes("@")) {
        domain = domain.split("@")[1];
      }
      return `https://generator.gsignature.com/?domain=${domain}`;
    },
  },
  created() {
    const savedInput = localStorage.getItem('brandFetcherLastInput');
    if (savedInput) {
      this.brandDomain = savedInput;
      this.fetched = true;
      this.lastFetchedDomain = savedInput;
      let domainPart = savedInput.toLowerCase();
      if (savedInput.includes('@')) {
        domainPart = savedInput.split('@')[1].toLowerCase();
      }
      const cacheKey = `brandCache_${domainPart}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        this.brandData = JSON.parse(cached);
      }
    }

    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    const domainFromUrl = urlParams.get('domain');

    if (tokenFromUrl) {
      this.token = tokenFromUrl;
      const payload = this.decodeTokenPayload(tokenFromUrl);
      if (payload) {
        this.handleTokenPayload(payload);
      }
    } else if (domainFromUrl) {
      this.brandDomain = domainFromUrl;
      if (!this.isSettings) {
        this.handleFetchBrandData();
      }
    }

    window.addEventListener('storage', (e) => {
      if (e.key === 'generatorAuthToken') {
        this.token = e.newValue;
        if (e.newValue) {
          const payload = this.decodeTokenPayload(e.newValue);
          if (payload) {
            this.handleTokenPayload(payload);
          }
        }
      }
    });

    this.tokenValidatedHandler = (e) => {
      const token = e.detail.token;
      this.token = token;
      const payload = this.decodeTokenPayload(token);
      if (payload) {
        this.handleTokenPayload(payload);
      }
    };
    window.addEventListener('token-validated', this.tokenValidatedHandler);
  },
  mounted() {
    const storedToken = localStorage.getItem('generatorAuthToken');
    if (storedToken) {
      const payload = this.decodeTokenPayload(storedToken);
      if (payload) {
        this.handleTokenPayload(payload);
      }
    }

    if (this.isSettings) {
      this.isFirstVisit = false;
    }
  },
  beforeUnmount() {
    if (this.tokenValidatedHandler) {
      window.removeEventListener('token-validated', this.tokenValidatedHandler);
    }
  },
  methods: {
    decodeTokenPayload(token) {
      try {
        const parts = token.split('.');
        if (parts.length !== 3) return null;
        const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(
          decodeURIComponent(
            payload
              .split('')
              .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join('')
          )
        );
      } catch (e) {
        console.error('Failed to decode token payload', e);
        return null;
      }
    },

    handleInputClick() {
      if (this.isFirstVisit) {
        this.isFirstVisit = false;
        localStorage.setItem("brandFetcherInputClicked", "true");
      }
    },
    async handleFetchBrandData() {
      if (!this.brandDomain) {
        return;
      }

      if (this.token && this.isFirstVisit) {
        this.isFirstVisit = false;
        localStorage.setItem("brandFetcherInputClicked", "true");
      }

      localStorage.setItem('brandFetcherLastInput', this.brandDomain);

      const disallowedDomains = [
        "gmail.com", "yahoo.com", "outlook.com", "hotmail.com",
        "live.com", "aol.com", "icloud.com", "msn.com",
      ];
      let domainPart = this.brandDomain.toLowerCase();
      let skipFetch = disallowedDomains.includes(domainPart);

      if (this.brandDomain.includes("@")) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(this.brandDomain)) {
          this.errorMessage = this.$t('brandFetcher.invalidEmailFormat');
          return;
        }
        domainPart = this.brandDomain.split("@")[1].toLowerCase();
        skipFetch = disallowedDomains.includes(domainPart);
      }

      this.errorMessage = "";
      if (skipFetch) {
        this.errorMessage = this.$t('brandFetcher.nonCompanyDomain');
        setTimeout(() => { this.errorMessage = ""; }, 5000);
      }

      this.isFetching = true;
      try {
        await this.fetchBrandData(domainPart, skipFetch);
        this.lastFetchedDomain = this.brandDomain;
      } finally {
        this.isFetching = false;
      }
    },
    async fetchBrandData(domainPart, skipFetch = false) {
      try {
        const cacheKey = `brandCache_${domainPart}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          this.brandData = JSON.parse(cached);
          if (!skipFetch) {
            this.fetched = true;
            const currentDomain = this.brandDomain.toLowerCase();
            const lastDomain = this.lastFetchedDomain.toLowerCase();
            if (currentDomain !== lastDomain) {
              this.rollBrandData();
            }
          }
        } else {
          const lang = this.$i18n.locale;
          const queryParam = this.brandDomain.includes("@")
            ? `email=${encodeURIComponent(this.brandDomain)}`
            : `domain=${encodeURIComponent(domainPart)}`;
          const url =
            `${BACKEND_URL}/generator/brand-data?${queryParam}&lang=${encodeURIComponent(lang)}` +
            (skipFetch ? "&skipFetch=true" : "");

          const response = await axios.get(url);
          this.brandData = response.data;
          localStorage.setItem(cacheKey, JSON.stringify(this.brandData));
          if (!skipFetch) {
            this.fetched = true;
            const currentDomain = this.brandDomain.toLowerCase();
            const lastDomain = this.lastFetchedDomain.toLowerCase();
            if (currentDomain !== lastDomain) {
              this.rollBrandData();
            }
          }
        }
        this.lastFetchedDomain = this.brandDomain;
        if (!skipFetch) sessionStorage.setItem("brandEmail", this.brandDomain);
      } catch (error) {
        console.error("Error fetching brand data:", error);
        this.errorMessage = this.$t('brandFetcher.fetchError');
      }
    },
    handleRollBrandData() {
      if (!this.brandDomain) return;
      this.rollBrandData();
    },
    rollBrandData() {
      if (!this.brandData) return;

      const brandColors = Array.isArray(this.brandData.colors) ? this.brandData.colors : [];
      const brandLogos = Array.isArray(this.brandData.logos) ? this.brandData.logos : [];
      const brandImages = Array.isArray(this.brandData.images) ? this.brandData.images : [];
      const brandLinks = Array.isArray(this.brandData.links) ? this.brandData.links : [];

      const colorSet = this.pickCohesiveColors(brandColors);
      const updates = {};
      if (colorSet) {
        Object.assign(updates, colorSet);
      }

      const shadow = { ...this.formData, ...updates };

      if (brandLogos.length > 0) {
        const randomLogoIdx = Math.floor(Math.random() * brandLogos.length);
        const randomLogoObj = brandLogos[randomLogoIdx];
        if (Array.isArray(randomLogoObj.formats) && randomLogoObj.formats.length > 0) {
          updates.logoImage = randomLogoObj.formats[0].src;
        }
      }

      const bannerArray = brandImages.filter((img) => img.type === "banner");
      if (bannerArray.length > 0) {
        const bannerIdx = Math.floor(Math.random() * bannerArray.length);
        const bannerObj = bannerArray[bannerIdx];
        if (Array.isArray(bannerObj.formats) && bannerObj.formats.length > 0) {
          updates.banner = { ...this.formData.banner, url: bannerObj.formats[0].src };
        }
      }

      if (this.brandData.name) {
        updates.companyName = this.brandData.name;
      }

      const loc = this.brandData.company?.location;
      if (loc) {
        const parts = [loc.city, loc.state || loc.region, loc.country].filter(Boolean);
        if (parts.length) {
          updates.companyAddress = parts.join(', ');
        }
      }

      if (this.brandData.description) {
        updates.disclaimer = this.brandData.description;
      }

      if (brandLinks.length > 0) {
        const updatedSocialLinks = { ...this.formData.socialLinks };

        brandLinks.forEach(linkObj => {
          if (!linkObj.name || !linkObj.url) return;

          const nameLower = linkObj.name.toLowerCase();
          const socialKey = this.getSocialKeyFromName(nameLower);

          if (socialKey) {
            updatedSocialLinks[socialKey] = linkObj.url;
          }
        });

        updates.socialLinks = updatedSocialLinks;
      }

      const templatesAllowed = ["template2", "template3", "template7", "template8", "template13"];
      if (
        templatesAllowed.includes(shadow.selectedTemplate) &&
        brandColors.length > 0
      ) {
        let availableHexes = brandColors.map((c) => c.hex);
        const usedColors = [];
        if (updates.mainColor) usedColors.push(updates.mainColor);
        if (updates.secondaryColor) usedColors.push(updates.secondaryColor);
        if (updates.socialIconColor) usedColors.push(updates.socialIconColor);

        availableHexes = availableHexes.filter((hex) => !usedColors.includes(hex));
        if (availableHexes.length === 0) {
          availableHexes = brandColors.map((c) => c.hex);
        }
        updates.descriptionBackground =
          availableHexes[Math.floor(Math.random() * availableHexes.length)];
      }

      updates.__brandFetcher = true;
      this.$emit("update-form-data", updates);
    },
    pickCohesiveColors(brandColors) {
      if (!Array.isArray(brandColors) || brandColors.length === 0) {
        return null;
      }
      function isWhite(hex) {
        return hex.trim().toLowerCase() === "#ffffff";
      }
      function parseHexToRGB(hex) {
        let c = hex.replace(/^#/, "");
        if (c.length === 3) {
          c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
        }
        const num = parseInt(c, 16);
        return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
      }
      function colorDistance(hexA, hexB) {
        const c1 = parseHexToRGB(hexA);
        const c2 = parseHexToRGB(hexB);
        return Math.sqrt(
          Math.pow(c1.r - c2.r, 2) +
          Math.pow(c1.g - c2.g, 2) +
          Math.pow(c1.b - c2.b, 2)
        );
      }
      const allHex = brandColors.map((c) => c.hex);

      function pickRandomColor(allowWhite = false) {
        let available = allowWhite ? allHex : allHex.filter((hex) => !isWhite(hex));
        if (available.length === 0) {
          available = allHex;
        }
        const idx = Math.floor(Math.random() * available.length);
        return available[idx];
      }

      let availableNonWhite = brandColors.filter((c) => !isWhite(c.hex));
      let mainColorHex;
      if (availableNonWhite.length > 0) {
        const randomIndex = Math.floor(Math.random() * availableNonWhite.length);
        mainColorHex = availableNonWhite[randomIndex].hex;
      } else {
        const randomIndex = Math.floor(Math.random() * brandColors.length);
        mainColorHex = brandColors[randomIndex].hex;
      }

      let secondColorHex = mainColorHex;
      if (brandColors.length > 1) {
        let nonWhiteHexes = allHex.filter((hex) => !isWhite(hex));
        if (nonWhiteHexes.length === 0) {
          nonWhiteHexes = allHex;
        }
        let minDist = Infinity;
        let closestHex = mainColorHex;
        for (const candidateHex of nonWhiteHexes) {
          if (candidateHex === mainColorHex) continue;
          const dist = colorDistance(mainColorHex, candidateHex);
          if (dist < minDist) {
            minDist = dist;
            closestHex = candidateHex;
          }
        }
        secondColorHex = closestHex;
      }

      const borderColor = pickRandomColor(false);
      const plannedAbsenceColor = pickRandomColor(true);
      let plannedAbsenceTextColor = pickRandomColor(true);
      {
        let iterations = 0;
        while (plannedAbsenceTextColor === plannedAbsenceColor && iterations < 10) {
          plannedAbsenceTextColor = pickRandomColor(true);
          iterations++;
        }
      }
      const socialIconColor = pickRandomColor(false);
      {
        let iterations = 0;
        while (secondColorHex === socialIconColor && iterations < 10) {
          secondColorHex = pickRandomColor(false);
          iterations++;
        }
      }
      let meetingColor = pickRandomColor(true);
      {
        let iterations = 0;
        while (meetingColor === socialIconColor && iterations < 10) {
          meetingColor = pickRandomColor(true);
          iterations++;
        }
      }

      const { r, g, b } = (function (hex) {
        let c = hex.replace(/^#/, "");
        if (c.length === 3) {
          c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
        }
        const num = parseInt(c, 16);
        return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
      })(socialIconColor);
      const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
      const isWhiteIcons = luminance < 128;

      return {
        mainColor: mainColorHex,
        secondaryColor: secondColorHex,
        borderColor: borderColor,
        plannedAbsenceColor: plannedAbsenceColor,
        plannedAbsenceTextColor: plannedAbsenceTextColor,
        meetingColor: meetingColor,
        contactIconColor: socialIconColor,
        socialIconColor: socialIconColor,
        addonsColor: pickRandomColor(false),
        chatColor: socialIconColor,
        isWhiteIcons: isWhiteIcons,
        isWhiteContactIcons: isWhiteIcons,
      };
    },
    getSocialKeyFromName(name) {
      const socialMappings = {
        'x': 'x',
        'x (': 'x',
        ' x': 'x',
        'twitter': 'x',
        'facebook': 'facebook',
        'linkedin': 'linkedin',
        'instagram': 'instagram',
        'behance': 'behance',
        'coffee': 'coffee',
        'diaspora': 'diaspora',
        'github': 'github',
        'gitlab': 'gitlab',
        'gitter': 'gitter',
        'heart': 'heart',
        'mastodon': 'mastodon',
        'menu': 'menu',
        'ok': 'ok',
        'reddit': 'reddit',
        'skype': 'skype',
        'slack': 'slack',
        'stackoverflow': 'stackoverflow',
        'telegram': 'telegram',
        'vk': 'vk',
        'whatsapp': 'whatsapp',
        'www': 'www',
        'xing': 'xing',
        'youtube': 'youtube'
      };

      for (const [key, value] of Object.entries(socialMappings)) {
        if (name.includes(key)) {
          return value;
        }
      }
      return null;
    },
    trackUserModification(field) {
      this.userModifiedFields.add(field);
    },
    resetUserModifications() {
      this.userModifiedFields.clear();
    },
    handleTokenPayload(payload) {
      const isSettingsPath = this.isSettings;

      let newBrandDomain = '';
      if (payload.email && payload.domain) {
        this.originalEmail = payload.email;
        if (payload.email.endsWith('@gsignature.com')) {
          const username = payload.email.split('@')[0];
          newBrandDomain = `${username}@${payload.domain}`;
        } else {
          newBrandDomain = payload.email;
        }
      } else if (payload.domain) {
        newBrandDomain = payload.domain;
      } else if (payload.email) {
        newBrandDomain = payload.email;
              }

        if (newBrandDomain) {
        if (payload.email && payload.domain) {
          const currentDomain = this.brandDomain.toLowerCase();
          const newDomain = newBrandDomain.toLowerCase();
          if (currentDomain !== newDomain) {
            this.brandDomain = newBrandDomain;
            this.fetched = false;
            this.lastFetchedDomain = '';
            this.brandData = null;
            if (!isSettingsPath) {
              this.handleFetchBrandData();
            }
          } else {
            this.brandDomain = newBrandDomain;
            this.fetched = true;
          }
          return;
        }

        if (!this.brandDomain) {
          this.brandDomain = newBrandDomain;
          this.fetched = false;
          if (!isSettingsPath) {
            this.handleFetchBrandData();
          }
        }
        else if (this.brandDomain.toLowerCase() === newBrandDomain.toLowerCase()) {
          this.brandDomain = newBrandDomain;
          this.fetched = true;
        }
        else {
          this.brandDomain = newBrandDomain;
          this.fetched = false;
          this.lastFetchedDomain = '';
          this.brandData = null;
          if (!isSettingsPath) {
            this.handleFetchBrandData();
          }
        }
      }
    },
  },
};
</script>

<style scoped>
.brand-fetcher {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
}

.input-container {
  position: relative;
  width: 100%;
}

.input-container input {
  min-width: 220px !important;
  padding: 0.6rem;
  flex: 1;
}

.brand-fetcher input.pulsating {
  border: 1px solid #ff6a00;
  animation: border-pulse 2s infinite;
}

@keyframes border-pulse {
  0% {
    border-color: #ff6a00;
  }

  50% {
    border-color: rgba(255, 106, 0, 0);
  }

  100% {
    border-color: #ff6a00;
  }
}

.copy-link {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 10px;
  cursor: pointer;
  color: #888;
}

.copy-link:hover {
  color: #ff6a00;
}

.brand-error-toast {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 5px;
  background-color: #e74c3c;
  color: #fff;
  padding: 10px 20px;
  border-radius: 5px;
  font-size: 14px;
  transition: all 0.6s;
  z-index: 1000;
}

.button-container {
  display: flex;
  gap: 0.5rem;
}

.action-button {
  min-width: 120px;
  padding: 0.5rem;
  text-align: center;
}

@media (max-width: 997px) {
  .brand-fetcher {
    margin-bottom: 1rem;
  }

  .input-container input {
    min-width: 100% !important;
  }
}
</style>
